import React from "react";
import { ImagePlus } from "lucide-react";
import {
  RUBRO_BELLEZA,
  esRubroHogarDeco,
  esRubroModa,
  esRubroMuebles,
} from "../lib/rubro";

/**
 * Dos productos REALES del rubro de la marca, para los costados del abanico. Todos de
 * `public/` (las fotos de producto del onboarding y de "Subir productos"). Sin rubro
 * conocido, uno de moda y uno de belleza.
 */
function productosDelRubro(rubro: string | null): [string, string] {
  if (esRubroModa(rubro))
    return [
      "/images/onboarding/rubros/moda/cartera-verde/product.jpg",
      "/images/multi-vistas/zapatillas/perspectiva.webp",
    ];
  if (rubro === RUBRO_BELLEZA)
    return [
      "/images/onboarding/rubros/belleza/lapiz/product.jpg",
      "/images/onboarding/rubros/belleza/mascara/product.jpg",
    ];
  if (esRubroHogarDeco(rubro) || esRubroMuebles(rubro))
    return [
      "/images/onboarding/rubros/hogar/silla/product.jpg",
      "/images/onboarding/rubros/hogar/mesa-luz/product.jpg",
    ];
  if (rubro === "Gastronomía")
    return [
      "/images/onboarding/rubros/gastronomia/hamburguesa/product.jpg",
      "/images/onboarding/rubros/gastronomia/pizza/product.jpg",
    ];
  return [
    "/images/onboarding/rubros/moda/cartera-verde/product.jpg",
    "/images/onboarding/rubros/belleza/lapiz/product.jpg",
  ];
}

/**
 * La primera tarjeta de la grilla de productos de Mi marca: lleva a "Subir productos" —
 * ADR-2026-10-13-mi-marca-tarjeta-subir-con-productos-reales (supersede lo visual de
 * ADR-2026-10-05-mi-marca-tarjeta-de-subir-producto).
 *
 * Antes era un abanico de tres "fotos" vacías con un +, y no se entendía qué había que
 * subir. Ahora el abanico son dos productos reales del rubro de la marca a los costados y,
 * al centro, un placeholder con forma de tarjeta de producto y la etiqueta "Tu producto"
 * (ADR-2026-10-13-mi-marca-tarjeta-subir-placeholder): "tus productos, y el que falta". Al pasar el mouse el abanico se abre. Cuadrada como las fotos de la grilla, así
 * la fila no cambia de alto. Sin botón: la tarjeta entera es el botón y el título dice la
 * acción (con botón, a ~236px de ancho, el abanico quedaba apretado). En el teléfono (la
 * tarjeta mide ~170px) va sólo el título; la bajada aparece desde `sm`.
 *
 * Colores en hex y no `brand-primary/10`: los tokens `brand-*` no aceptan opacidad.
 */
export const TarjetaSubirProducto: React.FC<{
  rubro: string | null;
  onClick: () => void;
}> = ({ rubro, onClick }) => {
  const [izquierda, derecha] = productosDelRubro(rubro);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex aspect-square w-full flex-col justify-center overflow-hidden rounded-2xl border-[1.5px] border-dashed border-[#B9C6FF] bg-gradient-to-b from-[#EEF1FF] to-[#F8F9FF] text-center transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
    >
      {/* Brillo de arriba, para que no sea un plano. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgba(61,92,255,0.14),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
      />

      {/* El abanico: dos productos reales y, al centro, el lugar para el tuyo. */}
      {/* Alto fijo (44% de la tarjeta, lo que mide el abanico con su etiqueta) y no `flex-1`:
          estirado, empujaba el texto al borde de abajo y dejaba un hueco en el medio. Así
          abanico y texto van juntos, centrados. */}
      <span aria-hidden className="relative block h-[44%] shrink-0">
        {[izquierda, derecha].map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            loading="lazy"
            className={`absolute left-1/2 top-1/2 aspect-[4/5] w-[26%] rounded-xl bg-white object-cover shadow-[0_10px_22px_-10px_rgba(16,24,40,0.45)] ring-[3px] ring-white transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none ${
              i === 0
                ? "-translate-x-[148%] -translate-y-1/2 -rotate-[9deg] group-hover:-translate-x-[160%] group-hover:-rotate-[12deg]"
                : "translate-x-[48%] -translate-y-1/2 rotate-[9deg] group-hover:translate-x-[60%] group-hover:rotate-[12deg]"
            }`}
          />
        ))}
        {/* Al centro, el lugar para el producto del usuario: la típica imagen de placeholder
            (el ícono de imagen con +) con un brillo que la recorre y la etiqueta "Tu producto"
            — ADR-2026-10-13-mi-marca-tarjeta-subir-placeholder-2. */}
        <span className="absolute left-1/2 top-1/2 z-10 flex aspect-[4/5] w-[29%] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-[0_14px_28px_-12px_rgba(22,68,235,0.45)] ring-[3px] ring-white transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] group-hover:scale-[1.06] motion-reduce:transition-none">
          <span className="relative flex flex-1 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-b from-[#E2E8FF] to-[#EEF2FF]">
            <ImagePlus className="h-[46%] w-[46%] text-[#7C8FFF]" strokeWidth={1.5} />
            <span className="brillo-de-edicion" />
          </span>
          <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-primary px-2 py-[3px] text-[9.5px] font-semibold leading-none text-white shadow-[0_4px_10px_-2px_rgba(22,68,235,0.5)] sm:text-[10.5px]">
            Tu producto
          </span>
        </span>
      </span>

      {/* El texto: qué es y para qué. */}
      <span className="relative mt-4 block px-3 sm:mt-8 sm:px-4">
        <span className="block text-[13.5px] font-semibold leading-tight text-[#16181D] sm:text-[15px]">
          Subí tus productos
        </span>
        <span className="mt-1 hidden text-[12.5px] leading-snug text-black/50 sm:block">
          Sumá sus fotos y usalos para crear en Atlas.
        </span>
      </span>
    </button>
  );
};
